/** Device status constants matching backend ACC+speed logic:
 *  - online:  ACC on, speed > 0 (engine running, moving)
 *  - idle:    ACC on, speed = 0 (engine running, stopped)
 *  - parked:  ACC off, speed = 0 (engine off, stationary)
 *  - towing:  ACC off, speed > 0 (engine off but moving — security alert)
 *  - offline: no data received within timeout
 */

export function statusColor(s: string): string {
  switch (s) {
    case 'online':  return '#8CC63F' // leaf green
    case 'idle':    return '#F9B233' // golden yellow
    case 'parked':  return '#34A8E0' // sky blue
    case 'towing':  return '#ef4444' // red
    default:        return '#94a3b8' // grey (offline)
  }
}

export function statusLabel(s: string): string {
  switch (s) {
    case 'online':  return 'Online'
    case 'idle':    return 'Idle'
    case 'parked':  return 'Parked'
    case 'towing':  return 'Towing'
    case 'offline': return 'Offline'
    default:        return s
  }
}

/**
 * Whether a device counts as "online" for summary counts.
 *
 * `online` and `idle` both mean the ignition is on and the device is
 * reporting; `parked` and `towing` are reporting but not running, and
 * `offline` has gone silent past the timeout.
 *
 * Keep every "N online" figure going through this so summaries can't drift
 * from the per-device status shown in the devices table.
 */
export function isOnlineStatus(s: string): boolean {
  return s === 'online' || s === 'idle'
}

/** Count of devices considered online, by {@link isOnlineStatus}. */
export function countOnline(devices: Array<{ status?: string }>): number {
  return devices.filter((d) => isOnlineStatus(d.status ?? 'offline')).length
}

/** Sort priority: towing first (security), then online, idle, parked, offline last */
export function statusSortOrder(s: string): number {
  switch (s) {
    case 'towing':  return 0
    case 'online':  return 1
    case 'idle':    return 2
    case 'parked':  return 3
    default:        return 4 // offline
  }
}
